/**
 * Pure helpers for the Job Detail Rating tab.
 *
 * Two jobs, both kept out of JSX so they can be tested directly rather than
 * through a rendered card: turning the monolith's review document into the
 * shape the professional card renders, and deciding what an absent value looks
 * like on screen.
 */

/**
 * A card value, or `N/A` when there is nothing to show.
 *
 * Both cards render their full row structure whether or not a review exists, so
 * every field needs the same fallback rather than each deciding for itself. The
 * design shows `N/A` in the free-text rows (Figma `7272-7145`) and GOTU-7339
 * extends that to every value.
 *
 * **A genuine `0` is a score, not a gap.** An office can rate a professional 0
 * on a skill, so a number is returned as-is and only a non-finite one falls
 * back — `!value` would turn a real 0 into `N/A` and quietly overstate the
 * rating. `NaN` and `Infinity` fall back too, so neither can reach a cell as
 * text.
 *
 * Whitespace-only strings count as absent: a feedback field containing a stray
 * space is a blank cell, not content.
 *
 * @param {string | number | null | undefined} value
 * @returns {string | number}
 */
export const orNotAvailable = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 'N/A';

  return value?.trim() ? value : 'N/A';
};

/**
 * The score shown beside the professional's name.
 *
 * The card this replaces was inconsistent about it: `professionalRating
 * .averageRating` for the heading, falling back to `rating_count` when
 * `professionalRating` is absent — the shape of a review captured before the
 * three sub-scores existed. Both are kept, in that order, so an old review
 * still shows the score it has, but the choice is made here once instead of
 * drifting between two call sites.
 */
const toRating = (review) => {
  const candidate = review?.professionalRating?.averageRating ?? review?.rating_count;

  return Number.isFinite(candidate) ? candidate : null;
};

/**
 * Maps the office's review *of the professional* for the Rating tab's
 * right-hand card.
 *
 * This side stays on the monolith. `state.jobs.rating` comes from
 * `GET admin/jobs/{id}`, and its `toHygienist` key is the office→professional
 * review — a direction organization-service does not hold at all, since
 * `location_review` is professional→office only and no ticket in the epic
 * creates an API for the other way round. So there is no domain layer to reach
 * for here, and a local mapper is the honest shape: one pure function, tested,
 * rather than four optional chains in JSX.
 *
 * **The fields are nested, and that is not a mistake.** `admin/jobs/{id}`
 * returns the raw review document, so the three skills live under
 * `professionalRating`. Do not copy `UserReview.js`'s flat reads
 * (`review.professionalSkills`) — those work only because the monolith's
 * `reviewDocToResponse` spreads `professionalRating` onto rows for the
 * *reviews list* endpoint. Same data, two shapes, and the flat one reads as
 * `undefined` here with no error.
 *
 * @param {Object} [rating] - `state.jobs.rating`
 * @returns {{
 *   professionalName: string,
 *   rating: number | null,
 *   professionalSkills: number | null,
 *   interpersonalSkills: number | null,
 *   timeManagement: number | null,
 *   confidentialFeedback: string,
 * } | null} null when the office never reviewed this professional
 */
export const toProfessionalReviewCardModel = (rating) => {
  const review = rating?.toHygienist;

  // The common case, not an error: an office often never rates the
  // professional. Null flows through to the card, which still renders its full
  // structure with `N/A` in every row — so this returning null is not the same
  // thing as the card having nothing to show.
  if (!review) return null;

  const skills = review.professionalRating;

  return {
    professionalName: review.rating_to?.full_name ?? '',
    rating: toRating(review),

    // Null rather than 0 for an absent sub-score: `0` is a score an office
    // could actually give, so it must not stand in for "not answered".
    // `orNotAvailable` is what turns the null into `N/A` at render time.
    professionalSkills: skills?.professionalSkills ?? null,
    interpersonalSkills: skills?.interpersonalSkills ?? null,
    timeManagement: skills?.timeManagement ?? null,

    // Kept as-is; the card decides what a blank renders as.
    confidentialFeedback: review.feedback_for_tempmee ?? '',
  };
};
